import { useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSession } from '../state/session.tsx';

/**
 * A submitted creation can finish after its form has gone away. Keep the
 * server result, but let only the view that submitted it navigate or show
 * its errors. Each activation is distinct, including A → B → A handoffs.
 *
 * BrowserRouter applies history updates with startTransition, so React may
 * still show the old view as mounted briefly after Cancel or Back. Compare
 * against the browser URL and React Router's history entry ({ key, idx } in
 * window.history.state) too: pushState/replaceState/popstate update those
 * immediately, before that transition commits. Same pathname+search with a
 * different entry key (Back/Forward between duplicate pushes) must drop the
 * activation.
 */
type RouterHistoryState = { key?: string; idx?: number } | null;

function browserHistoryEntry() {
  const state = window.history.state as RouterHistoryState;
  return {
    key: state?.key ?? null,
    idx: typeof state?.idx === 'number' ? state.idx : null,
  };
}

export function useCreationView(context: readonly unknown[]) {
  const location = useLocation();
  const { user, company, currentLocation, impersonatedBy, can } = useSession();
  const identity = JSON.stringify([
    location.key,
    user?.id,
    user?.role,
    user?.permissions,
    company?.id,
    company?.effectivePlan,
    company?.planFeatures,
    company?.accessLocked,
    currentLocation?.id,
    impersonatedBy,
    can('quotes:write'),
    can('schedule:write'),
    context,
  ]);
  const view = useRef({ identity, mounted: false });
  if (view.current.identity !== identity) view.current = { identity, mounted: false };
  const activation = view.current;
  useLayoutEffect(() => {
    activation.mounted = true;
    return () => {
      activation.mounted = false;
    };
  }, [activation]);

  return () => {
    const submitted = view.current;
    // MemoryRouter tests leave window.location and history.state alone;
    // BrowserRouter Cancel/Back change them synchronously. Either signal is
    // enough to drop the activation.
    const submittedUrl = `${window.location.pathname}${window.location.search}`;
    const submittedHistory = browserHistoryEntry();
    return () => {
      const history = browserHistoryEntry();
      return (
        submitted.mounted &&
        view.current === submitted &&
        `${window.location.pathname}${window.location.search}` === submittedUrl &&
        history.key === submittedHistory.key &&
        history.idx === submittedHistory.idx
      );
    };
  };
}
